<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    @click="$emit('click', $event)"
  >
    <template v-if="loading">
      <LoadingSpinner 
        size="sm" 
        color="slate" 
        :show-text="false"
        container-class="-ml-1"
      />
      {{ loadingText || 'Loading...' }}
    </template>
    <template v-else>
      <slot />
    </template>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import LoadingSpinner from '@/components/base/LoadingSpinner.vue'

interface Props {
  type?: 'button' | 'submit' | 'reset'
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'ghost' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  loading?: boolean
  loadingText?: string
  fullWidth?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  loadingText: '',
  fullWidth: false
})

const buttonClasses = computed(() => {
  const baseClasses = 'inline-flex items-center justify-center gap-1 font-semibold text-center transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-2.5 text-base'
  }
  
  const variantClasses = {
    primary: 'rounded-md bg-indigo-600 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-indigo-600',
    secondary: 'rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline-gray-500',
    danger: 'rounded-md bg-red-600 text-white shadow-sm hover:bg-red-500 focus-visible:outline-red-600',
    success: 'rounded-md bg-green-600 text-white shadow-sm hover:bg-green-500 focus-visible:outline-green-600',
    ghost: 'rounded-md text-gray-700 hover:bg-gray-100 focus-visible:outline-gray-500',
    outline: 'rounded-md border border-indigo-600 bg-transparent text-indigo-600 hover:bg-indigo-600 hover:text-white focus-visible:outline-indigo-600'
  }
  
  const widthClass = props.fullWidth ? 'w-full' : ''
  
  return [
    baseClasses,
    sizeClasses[props.size],
    variantClasses[props.variant],
    widthClass
  ].filter(Boolean).join(' ')
})

defineEmits<{
  click: [event: MouseEvent]
}>()
</script>
